import React, { FunctionComponent, KeyboardEvent, useEffect, useMemo } from 'react'
import { SafeAreaView } from 'react-native-safe-area-context'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  InfoSheetLinkDestinationKind,
  resolveInfoSheetLinkDestination,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/infoSheetLinks'
import { KEYS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { LinkPresentation } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import type { Link } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'

import { BodyText, Size } from '../../atoms/bodyText/BodyText'
import { FullWidthImage } from '../../atoms/fullWidthImage/FullWidthImage'
import { LinkIcon } from '../../atoms/icons/LinkIcon'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { StyledMarkdown } from '../../atoms/styledMarkdown/StyledMarkdown'
import { AccessibilityRolesNative, IS_WEB } from '../../constants'
import { getTypeStyles } from '../../styles/typeStyles'
import { resolveAssetTokens } from '../../utils/assetsHost'
import { tID } from '../../utils/utils'

import type { ThemeType } from '../../utils/themes/ThemeProvider'

export type InfoSheetLinkClickHandler = (
  resolvedUrl: string,
  link: Link,
  destinationKind: InfoSheetLinkDestinationKind,
) => void

export type InfoSheetBodyProps = {
  instructions?: string | null
  content?: string
  links?: Link[]
  imageURL?: string
  imageAlt?: string
  onLinkClicked: InfoSheetLinkClickHandler
  onView?: () => void
}

const ScrollContainer = styled.ScrollView`
  padding: 24px;
`

const ContentContainer = styled.View`
  margin-bottom: 32px;
`

const LinkContainer = styled.Pressable<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  padding: '16px',
  border: `1px solid ${theme.colors.borderDefault}`,
  borderRadius: '8px',
  marginBottom: '32px',
}))

const IconContainer = styled.View`
  margin-right: 16px;
`

const PrimaryCta = styled(PrimaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  height: 'auto',
  minHeight: theme.spacing['48px'],
  paddingVertical: theme.spacing['12px'],
  marginBottom: theme.spacing['32px'],
}))

const primaryCtaContentContainerStyle = { flexShrink: 1, width: '100%' } as const
const primaryCtaTextStyle = { flexShrink: 1, flexWrap: 'wrap' } as const

export const InfoSheetBody: FunctionComponent<InfoSheetBodyProps> = ({
  instructions = '',
  content,
  links,
  imageURL,
  imageAlt,
  onLinkClicked,
  onView = noop,
}) => {
  useEffect(() => {
    onView()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { colors, fontFamilyMetadata } = useTheme()
  const contentStyles = useMemo(
    () => ({ p3: { marginBottom: 10 }, h4: getTypeStyles(colors, fontFamilyMetadata).titleMedium }),
    [colors, fontFamilyMetadata],
  )
  const renderHero = Boolean(imageURL)
  const resolvedHeroUrl = useMemo(() => resolveAssetTokens(imageURL ?? ''), [imageURL])
  const resolvedLinks = useMemo(
    () =>
      (links ?? []).flatMap((link) => {
        const destination = resolveInfoSheetLinkDestination(link)
        if (!destination) {
          console.warn(`InfoSheetBody: link "${link.linkText}" has no valid URL or app route and will not be rendered`)
          return []
        }
        return [{ destination, link }]
      }),
    [links],
  )

  return (
    <SafeAreaView edges={['bottom']}>
      <ScrollContainer>
        {renderHero && <FullWidthImage url={resolvedHeroUrl} accessibilityLabel={imageAlt} />}
        {Boolean(instructions) && <StyledMarkdown content={instructions ?? ''} />}
        {Boolean(content) && (
          <ContentContainer>
            <StyledMarkdown content={content} customStyles={contentStyles} />
          </ContentContainer>
        )}
        {resolvedLinks.map(({ destination, link }, index) => {
          const handlePress = () => onLinkClicked(destination.url, link, destination.kind)

          if (link.presentation === LinkPresentation.PRIMARY_CTA) {
            const handleEnterKeyDown = (event: KeyboardEvent<Element>) => {
              if (event.key === KEYS.ENTER && !event.repeat) {
                event.preventDefault()
                handlePress()
              }
            }

            return (
              <PrimaryCta
                key={index}
                text={link.linkText}
                onPress={handlePress}
                onKeyDown={IS_WEB ? handleEnterKeyDown : undefined}
                fullWidth
                contentContainerStyle={primaryCtaContentContainerStyle}
                textStyle={primaryCtaTextStyle}
                accessibilityRole={AccessibilityRolesNative.LINK}
                accessibilityLabel={link.linkText}
                testID={tID('InfoSheetBody-primaryCta')}
              />
            )
          }

          return (
            <LinkContainer
              key={index}
              onPress={handlePress}
              testID={tID('InfoSheetBody-link')}
              accessibilityRole={AccessibilityRolesNative.LINK}
              accessibilityLabel={link.linkText}
            >
              <IconContainer>
                <LinkIcon size={35} />
              </IconContainer>
              <BodyText size={Size.SMALL} text={link.linkText} wrap />
            </LinkContainer>
          )
        })}
      </ScrollContainer>
    </SafeAreaView>
  )
}
